import { ServiceEnum } from "utils/enums/ServiceEnum";
import { CashFlowRequestEntity } from "./CashFlowRequestEntity";

export interface SelectOption {
  label?: string;
  value?: string;
}

export interface SearchFormValues {
  created?: Array<Record<string, unknown>>;
  type?: number | string;
  transRef?: string;
  supplierRefCode?: string;
  phoneNumber?: string;
  partnerCode?: string;
  userRef?: string[];
  storeCode?: string;
  code?: string;
}

export interface SearchContext {
  staffOptions: SelectOption[];
  userOptions: SelectOption[];
  selectedProviderCode?: string;
  selectedPartnerType: number;
  isStore: boolean;
  storeCode?: string;
  limit: number;
}

export class CashFlowQuery {
  static resolveUserRef(
    rawUserRef: string[] | undefined,
    staffOptions: SelectOption[],
    userOptions: SelectOption[],
    selectedPartnerType: number,
    isStore: boolean,
  ): string[] | undefined {
    if (!rawUserRef || rawUserRef.length === 0) return undefined;

    if (selectedPartnerType === 1 && isStore) {
      return rawUserRef.filter((ele) =>
        staffOptions?.some((child) => child?.value === ele),
      );
    }

    if (selectedPartnerType === 2 || (selectedPartnerType === 1 && !isStore)) {
      return rawUserRef.filter((ele) =>
        userOptions?.some((child) => child?.value === ele),
      );
    }

    return undefined;
  }

  static sanitize<T extends Record<string, unknown>>(obj: T): T {
    const result = { ...obj };
    Object.keys(result).forEach((key) => {
      const value = result[key];
      if (
        value === undefined ||
        value === null ||
        (typeof value === "string" && value.trim() === "")
      ) {
        delete result[key];
      }
    });
    return result as T;
  }

  static buildSearchQuery(
    formValues: SearchFormValues,
    context: SearchContext,
    dateFrom: string,
    dateTo: string,
  ): CashFlowRequestEntity {
    const finalUserRef = this.resolveUserRef(
      formValues.userRef,
      context.staffOptions,
      context.userOptions,
      context.selectedPartnerType,
      context.isStore,
    );

    const raw = new CashFlowRequestEntity({
      fromDate: dateFrom,
      toDate: dateTo,
      code: formValues.code,
      transRef: formValues.transRef,
      supplierRefCode: formValues.supplierRefCode,
      type:
        typeof formValues.type === "string"
          ? Number(formValues.type)
          : formValues.type,
      phoneNumber: formValues.phoneNumber,
      storeCodes: [context.storeCode ?? formValues.storeCode],
      userRef: finalUserRef,
      partnerCodes: [formValues.partnerCode],
      providerCode: context.selectedProviderCode,
      page: 0,
      limit: context.limit ?? 10,
      serviceCode: ServiceEnum.CASH_FLOW,
    });

    return this.sanitize(
      raw as unknown as Record<string, unknown>,
    ) as unknown as CashFlowRequestEntity;
  }

  static buildExportQuery(
    formValues: SearchFormValues,
    context: SearchContext,
    dateFrom: string,
    dateTo: string,
  ): CashFlowRequestEntity {
    const finalUserRef = this.resolveUserRef(
      formValues.userRef,
      context.staffOptions,
      context.userOptions,
      context.selectedPartnerType,
      context.isStore,
    );

    const raw = new CashFlowRequestEntity({
      fromDate: dateFrom,
      toDate: dateTo,
      code: formValues.code,
      transRef: formValues.transRef,
      supplierRefCode: formValues.supplierRefCode,
      type:
        typeof formValues.type === "string"
          ? Number(formValues.type)
          : formValues.type,
      phoneNumber: formValues.phoneNumber,
      storeCodes: [context.storeCode ?? formValues.storeCode],
      userRef: finalUserRef,
      partnerCodes: [formValues.partnerCode],
      providerCode: context.selectedProviderCode,
      serviceCode: ServiceEnum.CASH_FLOW,
    });

    return this.sanitize(
      raw as unknown as Record<string, unknown>,
    ) as unknown as CashFlowRequestEntity;
  }

  static buildTableChangeQuery(
    prevParams: CashFlowRequestEntity,
    formValues: Partial<SearchFormValues>,
    page: number,
    limit: number,
  ): CashFlowRequestEntity {
    const { created, ...rest } = formValues;
    const type =
      rest.type !== undefined
        ? typeof rest.type === "string"
          ? Number(rest.type)
          : rest.type
        : prevParams.type;

    const raw = new CashFlowRequestEntity({
      ...prevParams,
      ...rest,
      type: type as number | undefined,
      page,
      limit,
    });

    return this.sanitize(
      raw as unknown as Record<string, unknown>,
    ) as unknown as CashFlowRequestEntity;
  }

  static buildRedoQuery(
    fromDate: string,
    toDate: string,
  ): CashFlowRequestEntity {
    return new CashFlowRequestEntity({
      page: 0,
      limit: 10,
      fromDate,
      toDate,
      serviceCode: ServiceEnum.CASH_FLOW,
    });
  }
}
